import { AfterViewInit, Component, EventEmitter, HostListener, Input, OnInit, Output, ViewChild, OnDestroy } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { faGhost, faSkullCrossbones } from '@fortawesome/free-solid-svg-icons';
import { User } from 'src/app/models/user.model';
import { TimezoneService } from 'src/app/services/timezone.service';
import { likeRatioLabel, likeRatioRowClass } from 'src/app/utils/like-ratio';
import { ProfileVideo } from 'src/app/models/profile-video.model';
import { Subscription } from 'rxjs';

@Component({
  standalone: false,
  selector: 'sa-users-table',
  templateUrl: './users-table.component.html',
  styleUrls: ['./users-table.component.scss']
})
export class UsersTableComponent implements AfterViewInit, OnInit, OnDestroy {
  constructor(public timezoneService: TimezoneService) { }

  faGhost = faGhost;
  faSkullCrossbones = faSkullCrossbones;

  @Input()
  displayedColumns: string[] = ['profile_image', 'actions', 'name', 'birth', 'gender', 'is_mock_user', 'country', 'createdAt', 'last_activity', 'active', 'subscription_tier', 'hidden_under_review'];
  
  @Input()
  displayedColumnsMobile: string[] = ['profile_image_mobile', 'actions_mobile', 'name'];

  @Input()
  data: User[] = [];

  @Input()
  initialSort: Sort = { active: 'createdAt', direction: 'desc' };

  // When the "Incomplete onboarding" filter is on the API returns only
  // incomplete-onboarding users, so every visible row is highlighted yellow.
  @Input()
  highlightIncomplete = false;

  @Output() userClicked = new EventEmitter<User>();
  @Output() userBanClicked = new EventEmitter<User>();
  @Output() userUnBanClicked = new EventEmitter<User>();
  @Output() userPutUnderReviewClicked = new EventEmitter<User>();
  @Output() userRemoveFromReviewClicked = new EventEmitter<User>();
  @Output() userSilentHideClicked = new EventEmitter<User>();
  @Output() userSilentUnhideClicked = new EventEmitter<User>();
  @Output() userDeleteClicked = new EventEmitter<User>();
  @Output() userBoostClicked = new EventEmitter<User>();
  @Output() userDeboostClicked = new EventEmitter<User>();

  // Sort change evnt
  @Output() sortChange = new EventEmitter<Sort>();

  /** Only /message-flags binds this — the flag column's drill-down button. */
  @Output() flagDetailsClicked = new EventEmitter<User>();

  @ViewChild(MatSort) sort: MatSort;

  public isMobileLayout = false;
  public timezoneAbbr: string = 'PST';
  private timezoneSubscription: Subscription | null = null;

  @HostListener("window:resize", [])
  onResize() {
    var width = window.screen.width;
    this.isMobileLayout = width < 567;
  }

  ngOnInit() {
    if (window.screen.width < 567) { // 768px portrait
      this.isMobileLayout = true;
    }

    this.timezoneAbbr = this.timezoneService.getCurrentTimezoneAbbr();
    this.timezoneSubscription = this.timezoneService.getTimezoneConfig().subscribe(() => {
      this.timezoneAbbr = this.timezoneService.getCurrentTimezoneAbbr();
    });
  }

  ngAfterViewInit() {
    this.sort.sortChange.subscribe((changedSort) => {
      this.sortChange.emit(changedSort);
    });
  }

  ngOnDestroy() {
    if (this.timezoneSubscription) {
      this.timezoneSubscription.unsubscribe();
    }
  }

  // Ratio helpers live in utils/like-ratio so /users, /reported-users,
  // /suspicious-users and /message-flags all compute them the same way.
  likeRatioLabel(row: User): string {
    return likeRatioLabel(row, 'received');
  }

  /** What this user hands out, rather than what they receive. */
  givenLikeRatioLabel(row: User): string {
    return likeRatioLabel(row, 'given');
  }

  likeRatioRowClass(row: User): string {
    return likeRatioRowClass(row);
  }

  /**
   * The profile's videos, newest slot first, or an empty list when the row was
   * loaded by an endpoint that does not carry them. Never returns undefined,
   * so the template can iterate it without a guard.
   */
  videosFor(row: User): ProfileVideo[] {
    return (row && row.profile_videos) || [];
  }

  /** Name for the player's title bar, so a dialog opened from a table is placed. */
  rowTitle(row: User): string {
    if (!row) return '';
    return `${row.name || ''} ${row.lastname || ''}`.trim() || `#${row.user_id}`;
  }

  isMockUser(row: User): boolean {
    return Boolean(row.is_mock_user ?? row.isMockUser);
  }

  trackByUserId(_index: number, row: User): number {
    return row.user_id;
  }

  // Inline placeholder shown only when a profile image truly fails to load
  // (e.g. the image API errors). Data-URI so it can't itself 404/503 or hit the
  // network. Images show a skeleton shimmer while loading (see .img-loading in scss).
  static readonly IMAGE_PLACEHOLDER =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">' +
        '<rect width="64" height="64" fill="#e0e0e0"/>' +
        '<circle cx="32" cy="25" r="12" fill="#b0b0b0"/>' +
        '<path d="M12 60c0-11 9-18 20-18s20 7 20 18z" fill="#b0b0b0"/>' +
      '</svg>'
    );

  onImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    // Image arrived — drop the skeleton shimmer and reveal it.
    img.classList.remove('img-loading');
    img.classList.add('img-loaded');
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    // Stop the shimmer and show the placeholder; guard against a loop if the
    // placeholder itself were ever to error.
    img.classList.remove('img-loading');
    img.classList.add('img-loaded');
    if (img.src !== UsersTableComponent.IMAGE_PLACEHOLDER) {
      img.src = UsersTableComponent.IMAGE_PLACEHOLDER;
    }
  }
}
